import { apiRequest } from '../api/client';

/** `rotating-menu/design.md` §3.4: one line per position — the server resolves the dish published in that position at generation time. */
export interface OrderTemplateItem {
  positionId: string;
  quantity: number;
  notes: string | null;
}

/** Mirrors `apps/api` `PortalOrderTemplatesController` (`StoredOrderTemplate`) — no price field exists on `OrderTemplate` at all, same criterion as `orders/api.ts`'s `Order`. */
export interface OrderTemplate {
  id: string;
  customerId: string;
  siteId: string;
  /** The unit whose contract prices the generated orders. Resolve its name through `GET /portal/me`. */
  operatingUnitId?: string;
  /** Unit name as the API joins it, so the list does not depend on `portal/me`. */
  operatingUnitName?: string;
  name: string;
  /** ISO weekday, 1 = Monday. */
  daysOfWeek: number[];
  isActive: boolean;
  items: OrderTemplateItem[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateOrderTemplateInput {
  siteId: string;
  /** Sent only when the customer has several units; the server infers it otherwise. */
  operatingUnitId?: string;
  name: string;
  daysOfWeek: number[];
  items: OrderTemplateItem[];
}

export interface UpdateOrderTemplateInput {
  name?: string;
  daysOfWeek?: number[];
  isActive?: boolean;
  items?: OrderTemplateItem[];
}

export function listOrderTemplates(): Promise<OrderTemplate[]> {
  return apiRequest<OrderTemplate[]>('/portal/order-templates');
}

export function createOrderTemplate(input: CreateOrderTemplateInput): Promise<OrderTemplate> {
  return apiRequest<OrderTemplate>('/portal/order-templates', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateOrderTemplate(
  id: string,
  input: UpdateOrderTemplateInput,
): Promise<OrderTemplate> {
  return apiRequest<OrderTemplate>(`/portal/order-templates/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function deleteOrderTemplate(id: string): Promise<void> {
  return apiRequest<void>(`/portal/order-templates/${id}`, { method: 'DELETE' });
}

/**
 * `rotating-menu/tasks.md` PR 11 gap: a day-independent source of
 * positions, unlike `orders/api.ts`'s `listAvailableMenus` (today's
 * published calendar only). Mirrors `apps/api`
 * `PortalMenuPositionsController` — `serviceTypeId` only, no nested
 * `serviceType` code/name (`design.md` §3.5).
 */
export interface OrderTemplatePosition {
  id: string;
  code: string;
  name: string;
  displayOrder: number;
  serviceTypeId: string;
}

export function listMenuPositions(operatingUnitId?: string): Promise<OrderTemplatePosition[]> {
  const query =
    operatingUnitId === undefined ? '' : `?operatingUnitId=${encodeURIComponent(operatingUnitId)}`;

  return apiRequest<OrderTemplatePosition[]>(`/portal/menu-positions${query}`);
}
